import { Component, EventEmitter, Input, Output, inject, OnInit, signal, effect } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { ExternalProvider } from '../../../models/auth.model';
import { AuthModalService } from '../../services/auth-modal.service';

import { logger } from '../../../helpers/logger';
@Component({
  selector: 'eng-login-form',
  imports: [ReactiveFormsModule],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
})
export class LoginFormComponent implements OnInit {
  // public readonly activeModal = inject(NgbActiveModal);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly toastService = inject(ToastService);
  private readonly cookieService = inject(CookieService);

  @Input() returnUrl = '';
  @Input() showRegisterLink = true;
  @Input() showForgotPasswordLink = true;
  @Output() success = new EventEmitter<boolean>();
  @Output() switchToRegisterRequested = new EventEmitter<void>();
  @Output() forgotPasswordRequested = new EventEmitter<void>();

  loginForm: FormGroup;
  isLoading = signal(false);
  isResendingConfirmation = signal(false);
  confirmationEmailSent = signal(false);
  externalProviders: ExternalProvider[] = [];
  antiForgeryToken = '';
  serverErrors: string[] = [];
  fieldErrors: { [key: string]: string[] } = {};
  unconfirmedEmail: string | null = null;

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      rememberMe: [false],
    });

    // Effect to handle form control disabled state when loading changes
    effect(() => {
      this.updateFormControlsDisabledState();
    });
  }

  ngOnInit(): void {
    this.loadExternalProviders();
    // Get the anti-forgery token from the cookie
    this.antiForgeryToken = this.cookieService.get('XSRF-TOKEN');
  }

  private updateFormControlsDisabledState(): void {
    if (this.loginForm) {
      if (this.isLoading()) {
        this.loginForm.get('email')?.disable();
        this.loginForm.get('password')?.disable();
        this.loginForm.get('rememberMe')?.disable();
      } else {
        this.loginForm.get('email')?.enable();
        this.loginForm.get('password')?.enable();
        this.loginForm.get('rememberMe')?.enable();
      }
    }
  }

  private loadExternalProviders(): void {
    this.authService.getExternalProviders().subscribe({
      next: providers => {
        this.externalProviders = providers;
      },
      error: error => {
        logger.error('Failed to load external providers:', error);
        this.externalProviders = [];
      },
    });
  }

  onSubmit(): void {
    if (this.loginForm.valid && !this.isLoading()) {
      this.isLoading.set(true);
      const formData = this.loginForm.value;

      this.authService
        .login({
          email: formData.email,
          password: formData.password,
          rememberMe: formData.rememberMe,
          returnUrl: this.returnUrl,
        })
        .subscribe({
          next: response => {
            this.isLoading.set(false);
            if (response.success) {
              this.success.emit(true);
              // Redirect or refresh as needed
              if (response.redirectUrl) {
                window.location.href = response.redirectUrl;
              } else {
                window.location.reload();
              }
            } else {
              this.toastService.error(response.message || 'Login failed');
            }
          },
          error: error => {
            this.isLoading.set(false);
            this.clearErrors();

            const errorBody = error.error;

            // Handle unconfirmed email specifically — show a targeted message with resend option
            if (errorBody?.reasonCode === 'UNCONFIRMED_EMAIL') {
              this.unconfirmedEmail = errorBody.email || this.loginForm.getRawValue().email;
              // Don't add to serverErrors — the template shows a dedicated banner instead
              logger.warn('Login blocked: email not confirmed for', this.unconfirmedEmail);
              return;
            }

            if (errorBody?.message) {
              this.serverErrors.push(errorBody.message);
            }
            if (errorBody?.errors && Array.isArray(errorBody.errors)) {
              this.serverErrors.push(...errorBody.errors);
            }
            if (this.serverErrors.length === 0) {
              if (error.status === 401) {
                this.serverErrors = ['Invalid email or password. Please try again.'];
              } else {
                this.serverErrors = ['Login failed. Please try again.'];
              }
            } else {
              // Also process potential ModelState errors if no message/errors
              this.processValidationErrors(errorBody);
            }

            logger.error('Login error:', error);
          },
        });
    }
  }

  private clearErrors(): void {
    this.serverErrors = [];
    this.fieldErrors = {};
    this.unconfirmedEmail = null;
    this.confirmationEmailSent.set(false);
  }

  resendConfirmationEmail(): void {
    if (!this.unconfirmedEmail || this.isResendingConfirmation() || this.confirmationEmailSent()) {
      return;
    }

    this.isResendingConfirmation.set(true);
    this.authService.resendConfirmationEmail(this.unconfirmedEmail).subscribe({
      next: response => {
        this.isResendingConfirmation.set(false);
        if (response.success) {
          this.confirmationEmailSent.set(true);
          this.toastService.success(response.message || 'Confirmation email sent.');
        } else {
          this.toastService.error(response.message || 'Failed to send confirmation email. Please try again.');
        }
      },
      error: error => {
        this.isResendingConfirmation.set(false);
        const message = error?.error?.message || 'Failed to send confirmation email. Please try again.';
        this.toastService.error(message);
        logger.error('Failed to resend confirmation email:', error);
      },
    });
  }

  private processValidationErrors(errorResponse: any): void {
    if (errorResponse && typeof errorResponse === 'object') {
      // Check for ModelState format: { "": ["error1", "error2"], "FieldName": ["error3"] }
      for (const key in errorResponse) {
        if (errorResponse.hasOwnProperty(key) && Array.isArray(errorResponse[key])) {
          if (key === '' || key === 'general') {
            // General errors go to serverErrors
            this.serverErrors.push(...errorResponse[key]);
          } else {
            // Field-specific errors go to fieldErrors
            const fieldName = this.mapFieldName(key);
            this.fieldErrors[fieldName] = errorResponse[key];

            // Also add field-specific errors to server errors for now
            // until we implement field-specific display
            this.serverErrors.push(...errorResponse[key]);
          }
        }
      }
    }
  }

  private mapFieldName(serverFieldName: string): string {
    // Map server field names to form control names
    const fieldMap: { [key: string]: string } = {
      Email: 'email',
      Password: 'password',
    };

    return fieldMap[serverFieldName] || serverFieldName.toLowerCase();
  }

  onExternalLogin(provider: string): void {
    if (!this.isLoading()) {
      this.isLoading.set(true);

      // Directly redirect to the challenge URL without calling the API first
      const challengeUrl = `/api/account/external-login-challenge?provider=${encodeURIComponent(provider)}&returnUrl=${encodeURIComponent(this.returnUrl)}`;

      // Close the modal and redirect to the external provider challenge URL
      this.success.emit(true);
      window.location.href = challengeUrl;
    }
  }

  switchToRegister(): void {
    if (this.showRegisterLink) {
      this.switchToRegisterRequested.emit();
    }
  }

  openRegisterModal(): void {
    this.success.emit(true);
    // Use the injected auth modal service to open register modal
    setTimeout(() => {
      this.authModalService.openRegisterModal(this.returnUrl);
    }, 100);
  }

  openForgotPassword(): void {
    if (this.showForgotPasswordLink) {
      this.forgotPasswordRequested.emit();
    }
  }
}
